/**
 * Universal Logger Configuration
 * 
 * Environment-aware configuration management for the Universal Logger System.
 * Follows existing patterns from ClassificationLogger and secure-env.
 */

import { UniversalLogLevel, LoggerConfig, EnvironmentConfig } from './types';

/**
 * Get environment-specific configuration for the Universal Logger
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const env = import.meta.env;
  
  // Determine environment
  const isTest = env.NODE_ENV === 'test';
  const isProduction = env.NODE_ENV === 'production' || env.PROD;
  const isDevelopment = env.NODE_ENV === 'development' || env.DEV || (!isTest && !isProduction);
  const environment = env.NODE_ENV || 'development';
  
  // Environment-specific defaults
  let defaults = {
    environment,
    isDevelopment,
    isProduction,
    isTest,
    logLevel: UniversalLogLevel.INFO,
    enableConsoleOutput: true,
    enableSentryIntegration: false,
    enablePerformanceMetrics: true,
    enableSensitiveDataFiltering: true,
    maxLogEntries: 10000
  };
  
  if (isDevelopment) {
    defaults = {
      ...defaults,
      logLevel: UniversalLogLevel.DEBUG,
      enableConsoleOutput: true,
      enableSentryIntegration: false,
      enablePerformanceMetrics: true,
      enableSensitiveDataFiltering: false, // Allow full logging in dev
      maxLogEntries: 1000
    };
  } else if (isProduction) {
    defaults = {
      ...defaults,
      logLevel: UniversalLogLevel.ERROR,
      enableConsoleOutput: false, // Critical: Disable console in production
      enableSentryIntegration: true,
      enablePerformanceMetrics: true,
      enableSensitiveDataFiltering: true, // Always filter in production
      maxLogEntries: 50000
    };
  } else if (isTest) {
    defaults = {
      ...defaults,
      logLevel: UniversalLogLevel.ERROR,
      enableConsoleOutput: false, // Disable console in tests
      enableSentryIntegration: false,
      enablePerformanceMetrics: false,
      enableSensitiveDataFiltering: false,
      maxLogEntries: 100
    };
  }
  
  // Override with environment variables using VITE_LOGGER_ prefix
  return {
    ...defaults,
    logLevel: (env.VITE_LOGGER_CONSOLE_LEVEL?.toLowerCase() as UniversalLogLevel) || defaults.logLevel,
    enableConsoleOutput: env.VITE_LOGGER_CONSOLE_ENABLED === 'true' ? true :
                        env.VITE_LOGGER_CONSOLE_ENABLED === 'false' ? false : defaults.enableConsoleOutput,
    enableSentryIntegration: env.VITE_LOGGER_SENTRY_ENABLED === 'true' ? true :
                            env.VITE_LOGGER_SENTRY_ENABLED === 'false' ? false : defaults.enableSentryIntegration,
    enablePerformanceMetrics: env.VITE_LOGGER_PERFORMANCE_TRACKING === 'true' ? true :
                             env.VITE_LOGGER_PERFORMANCE_TRACKING === 'false' ? false : defaults.enablePerformanceMetrics,
    enableSensitiveDataFiltering: env.VITE_LOGGER_SENSITIVE_DATA_FILTERING === 'true' ? true :
                                 env.VITE_LOGGER_SENSITIVE_DATA_FILTERING === 'false' ? false : defaults.enableSensitiveDataFiltering,
    maxLogEntries: parseInt(env.VITE_LOGGER_MAX_LOG_ENTRIES as string) || defaults.maxLogEntries
  };
}

/**
 * Create LoggerConfig from environment configuration
 */
export function createLoggerConfig(): LoggerConfig {
  const envConfig = getEnvironmentConfig();
  
  const env = import.meta.env;
  
  return {
    consoleEnabled: envConfig.enableConsoleOutput,
    consoleLevel: envConfig.logLevel,
    sentryEnabled: envConfig.enableSentryIntegration,
    sentryLevel: (env.VITE_LOGGER_SENTRY_LEVEL?.toLowerCase() as UniversalLogLevel) || UniversalLogLevel.ERROR,
    sentryIncludeWarnings: env.VITE_LOGGER_SENTRY_INCLUDE_WARNINGS === 'true' ? true : false,
    performanceTracking: envConfig.enablePerformanceMetrics,
    sensitiveDataFiltering: envConfig.enableSensitiveDataFiltering,
    queueEnabled: env.VITE_LOGGER_QUEUE_ENABLED === 'false' ? false : true, // Allow disabling queue for tests
    maxLogEntries: envConfig.maxLogEntries,
    queueConfig: {
      enabled: true,
      maxSize: envConfig.maxLogEntries,
      processingBatchSize: 50,
      processingIntervalMs: 100,
      overflowStrategy: 'drop-oldest',
      enableMetrics: true
    },
    environment: envConfig.environment
  };
}

/**
 * Check if a log level is enabled based on current configuration
 */
export function isLogLevelEnabled(level: UniversalLogLevel, config: EnvironmentConfig): boolean {
  const levels = [
    UniversalLogLevel.ERROR,   // 0
    UniversalLogLevel.WARN,    // 1
    UniversalLogLevel.INFO,    // 2
    UniversalLogLevel.DEBUG    // 3
  ];
  
  const currentLevelIndex = levels.indexOf(config.logLevel);
  const requestedLevelIndex = levels.indexOf(level);
  
  return requestedLevelIndex <= currentLevelIndex;
}

/**
 * Generate a unique correlation ID (deprecated - use CorrelationIdProvider)
 * @deprecated Use getCorrelationId from correlation-id.ts instead
 */
export function generateCorrelationId(): string {
  return `ulog_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get enhanced memory usage information
 */
export function getMemoryUsage(): { used: number; total: number; percentage: number; heapUsed: number; heapTotal: number; external: number; heapLimit?: number; timestamp: number } {
  const timestamp = performance.now();
  
  // Use performance.memory if available (Chrome/Edge), fallback to estimates
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    const used = memory.usedJSHeapSize;
    const total = memory.totalJSHeapSize;
    const limit = memory.jsHeapSizeLimit;
    const percentage = Math.round((used / total) * 100);
    
    return {
      used,
      total,
      percentage,
      // Node.js-style properties for test compatibility
      heapUsed: used,
      heapTotal: total,
      external: memory.usedJSHeapSize - memory.usedJSHeapSize, // Approximation
      heapLimit: limit,
      timestamp
    };
  }
  
  // Fallback for browsers without memory API or Node.js environment
  const fallbackMemory = 1024 * 1024 * 10; // 10MB estimation
  return {
    used: fallbackMemory,
    total: fallbackMemory * 10,
    percentage: 10,
    heapUsed: fallbackMemory,
    heapTotal: fallbackMemory * 10,
    external: 0,
    timestamp
  };
}

/**
 * Calculate memory delta between two memory snapshots
 */
export function calculateMemoryDelta(
  startMemory: { used: number; total: number; timestamp: number },
  endMemory: { used: number; total: number; timestamp: number }
): { usedDelta: number; totalDelta: number; timeDelta: number; usedDeltaMB: number } {
  const usedDelta = endMemory.used - startMemory.used;
  const totalDelta = endMemory.total - startMemory.total;
  const timeDelta = endMemory.timestamp - startMemory.timestamp;
  const usedDeltaMB = Math.round((usedDelta / (1024 * 1024)) * 100) / 100;
  
  return {
    usedDelta,
    totalDelta,
    timeDelta,
    usedDeltaMB
  };
}

/**
 * Check if memory usage exceeds warning thresholds
 */
export function checkMemoryThresholds(memoryUsage: { used: number; total: number; percentage: number }): {
  isWarning: boolean;
  isCritical: boolean;
  level: 'normal' | 'warning' | 'critical';
  message?: string;
} {
  const { percentage, used } = memoryUsage;
  const usedMB = Math.round((used / (1024 * 1024)) * 100) / 100;
  
  if (percentage >= 90) {
    return {
      isWarning: true,
      isCritical: true,
      level: 'critical',
      message: `Critical memory usage: ${percentage}% (${usedMB}MB)`
    };
  } else if (percentage >= 75) {
    return {
      isWarning: true,
      isCritical: false,
      level: 'warning',
      message: `High memory usage: ${percentage}% (${usedMB}MB)`
    };
  }
  
  return {
    isWarning: false,
    isCritical: false,
    level: 'normal'
  };
}

/**
 * Sanitize sensitive data from log details
 */
export function sanitizeSensitiveData(details: Record<string, any>): Record<string, any> {
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth', 'authorization',
    'cookie', 'session', 'credentials', 'apikey', 'api_key'
  ];
  
  const sanitized = { ...details };
  
  function sanitizeObject(obj: any, path = ''): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map((item, index) => sanitizeObject(item, `${path}[${index}]`));
    }
    
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      const fullPath = path ? `${path}.${key}` : key;
      
      if (sensitiveKeys.some(sensitiveKey => lowerKey.includes(sensitiveKey))) {
        result[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        result[key] = sanitizeObject(value, fullPath);
      } else {
        result[key] = value;
      }
    }
    
    return result;
  }
  
  return sanitizeObject(sanitized);
} 